import { UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { getDynamicRedirectUri, getDynamicPostLogoutRedirectUri } from '../utils/authUtils';

/**
 * Feature flag to enable/disable OIDC authentication option
 */
export const OIDC_ENABLED = process.env.REACT_APP_OIDC_ENABLED === 'true';

/**
 * OIDC Provider Configuration for renewmyday.com
 * 
 * This is an alternative authentication provider to Azure AD (Entra ID).
 * Both providers are supported and users can choose which one to use.
 * 
 * NOTE: We manually specify all endpoints to avoid CORS issues with the
 * discovery endpoint (.well-known/openid-configuration)
 */
export const oidcConfig: UserManagerSettings = {
  // Provider authority (issuer)
  authority: 'https://renewmyday.com',
  
  // Manually specify metadata to avoid CORS issues with discovery endpoint
  // These values are from https://renewmyday.com/.well-known/openid-configuration
  metadata: {
    issuer: 'https://renewmyday.com',
    authorization_endpoint: 'https://renewmyday.com/oauth/authorize',
    token_endpoint: 'https://renewmyday.com/oauth/token',
    jwks_uri: 'https://renewmyday.com/oauth/jwks',
    scopes_supported: ['openid', 'profile', 'email', 'phone', 'address'],
    response_types_supported: ['code', 'token'],
    subject_types_supported: ['public'],
    token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post'],
  },
  
  // Client configuration (SPA - no secret required)
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '6',
  
  // Redirect URIs - use same dynamic URIs as MSAL
  redirect_uri: getDynamicRedirectUri(),
  post_logout_redirect_uri: getDynamicPostLogoutRedirectUri(),
  
  // Response type for authorization code flow with PKCE
  // Note: Token exchange requires backend proxy due to CORS issues with provider
  response_type: 'code',
  
  // Scopes - same as MSAL login request
  scope: 'openid profile email',
  
  // Storage configuration
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  
  // Automatic token management
  automaticSilentRenew: false, // Disable since we're not using silent renew iframe
  
  // Load user info from userinfo endpoint
  loadUserInfo: false, // Disable if userinfo endpoint has CORS issues too
  
  // Monitor session state - disable to avoid CORS issues with check_session_iframe
  monitorSession: false,
  
  // Filter protocol claims from user profile
  filterProtocolClaims: true,
};

/**
 * OIDC login request configuration
 * Mirrors the MSAL loginRequest for consistency
 */
export const oidcLoginRequest = {
  scope: 'openid profile email',
};

/**
 * Check if OIDC authentication is available
 */
export const isOidcAvailable = (): boolean => {
  return OIDC_ENABLED && !!process.env.REACT_APP_OIDC_CLIENT_ID;
};
